import { BaseApiService } from '../../common/services/baseApiService';
import { API_URL } from '../../common/utils/util';
import {
  Dispositivo,
  ListaDispositivos,
  PeticionCierre,
  RespuestaCierre,
  RespuestaSonar,
} from '../types/dispositivos.types';

/** La acción que falló: el mismo código HTTP significa cosas distintas en cada una. */
export type AccionDispositivos = 'listar' | 'etiquetar' | 'sonar' | 'cerrar-sesion' | 'reactivar';

/** Lo que manda Nest en el cuerpo de un error. */
interface CuerpoError {
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

/** Textos por omisión de Nest, en inglés: delante de TI no explican nada. */
const GENERICOS_NEST = new Set([
  'Forbidden resource',
  'Forbidden',
  'Unauthorized',
  'Not Found',
  'Too Many Requests',
  'ThrottlerException: Too Many Requests',
  'Internal server error',
  'Bad Request',
]);

function mensajeDeLaApi(data: CuerpoError | null | undefined): string | null {
  const m = data?.message;
  if (Array.isArray(m)) {
    const partes = m.filter((x): x is string => typeof x === 'string' && x.trim() !== '');
    return partes.length ? partes.join(' · ') : null;
  }
  if (typeof m === 'string' && m.trim() !== '' && !GENERICOS_NEST.has(m.trim()) && !/^Cannot (GET|POST|PATCH)/.test(m)) {
    return m;
  }
  return null;
}

/**
 * Error de `/dispositivos` que conserva el código HTTP.
 *
 * `BaseApiService.handleResponse` sólo se queda con `message`, y aquí no
 * basta: un 429 al «Hacer sonar» es el límite de 1 cada 10 s por equipo
 * (§4.6) y se le dice así a TI, no «Request failed with status 429».
 * `status = 0` es que no hubo respuesta HTTP.
 */
export class DispositivosApiError extends Error {
  readonly status: number;
  readonly accion: AccionDispositivos;

  constructor(status: number, accion: AccionDispositivos, data?: CuerpoError | null) {
    super(DispositivosApiError.mensajeDe(status, accion, data));
    this.name = 'DispositivosApiError';
    this.status = status;
    this.accion = accion;
  }

  /** 429: el límite de «Hacer sonar» (1 cada 10 s por equipo). */
  get esLimite(): boolean {
    return this.status === 429;
  }

  static mensajeDe(status: number, accion: AccionDispositivos, data?: CuerpoError | null): string {
    if (status === 0) return 'No hubo respuesta de la API. Revisa la conexión y vuelve a intentarlo.';
    if (status === 429) {
      return accion === 'sonar'
        ? 'Ya se hizo sonar este equipo hace menos de 10 segundos. Espera un momento y vuelve a intentarlo.'
        : 'Demasiadas peticiones seguidas. Espera unos segundos y vuelve a intentarlo.';
    }
    const propio = mensajeDeLaApi(data);
    if (status === 404) {
      // Un 404 de la LISTA es que la ruta no existe: la API de este entorno es
      // anterior a la Fase 1. En una acción, que el equipo ya no está.
      if (accion === 'listar') {
        return 'La API de este entorno todavía no tiene el registro de equipos (falta desplegar la Fase 1).';
      }
      return propio ?? 'Ese equipo ya no está en el registro (pudo purgarse por inactividad). Actualiza la lista.';
    }
    if (status === 403) {
      if (propio) return propio;
      return accion === 'cerrar-sesion' || accion === 'reactivar'
        ? 'Sólo TI (superadmin o admin) puede cerrar la sesión de un equipo o reactivarlo.'
        : 'Tu cuenta no tiene permiso para ver los equipos de esta tienda.';
    }
    if (propio) return propio;
    if (status >= 500) return `El servidor no pudo responder (${status}). Reintenta en un momento.`;
    return `La petición falló (${status}).`;
  }
}

const BASE = `${API_URL}/dispositivos`;
const seg = (s: string): string => encodeURIComponent(s);

export interface FiltroDispositivos {
  /** Para un gerente se omiten: la API fuerza su tienda e ignora la consulta. */
  cadena?: string;
  sucursal?: number;
  dias: number;
}

/** La consulta de `GET /dispositivos`. Exportada para fijarla en pruebas. */
export function consultaDispositivos(f: FiltroDispositivos): string {
  const q = new URLSearchParams();
  if (f.cadena) q.set('cadena', f.cadena);
  if (f.sucursal !== undefined && f.sucursal !== null) q.set('sucursal', String(f.sucursal));
  q.set('dias', String(f.dias));
  return `?${q.toString()}`;
}

/**
 * `/dispositivos` (módulo nuevo de la API). Un método por ruta, con el patrón
 * de `ErpService` y `VendedoresService`.
 *
 * La pantalla nunca filtra por seguridad: la API decide qué tienda ve el
 * gerente y quién puede cerrar sesiones (§4.6).
 */
export class DispositivosService extends BaseApiService {
  private static async pedir(
    accion: AccionDispositivos,
    ruta: string,
    init: { method: 'GET' | 'POST' | 'PATCH'; body?: unknown },
  ): Promise<Response> {
    let response: Response;
    try {
      response = await fetch(`${BASE}${ruta}`, {
        method: init.method,
        headers: this.getAuthHeaders(),
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
      });
    } catch {
      throw new DispositivosApiError(0, accion);
    }
    if (!response.ok) {
      this.handleAuthError(response);
      const data = (await response.json().catch(() => ({}))) as CuerpoError;
      throw new DispositivosApiError(response.status, accion, data);
    }
    return response;
  }

  /** Lee el cuerpo si hay; un 204 o un cuerpo vacío dan `null`. */
  private static async cuerpo<T>(response: Response): Promise<T | null> {
    if (response.status === 204) return null;
    const texto = await response.text().catch(() => '');
    if (!texto) return null;
    try {
      return JSON.parse(texto) as T;
    } catch {
      return null;
    }
  }

  static async listar(f: FiltroDispositivos): Promise<ListaDispositivos> {
    const r = await this.pedir('listar', consultaDispositivos(f), { method: 'GET' });
    const data = await this.cuerpo<ListaDispositivos>(r);
    return {
      resumen: data?.resumen ?? { vistos: 0, enPantalla: 0, conexionAbierta: 0, revocados: 0, nuevos24h: 0 },
      dispositivos: Array.isArray(data?.dispositivos) ? data!.dispositivos : [],
      apiIniciadaEn: data?.apiIniciadaEn ?? null,
    };
  }

  /** `null` borra la etiqueta. La API no fija qué responde: se devuelve lo que llegue. */
  static async etiquetar(id: string, etiqueta: string | null): Promise<Partial<Dispositivo> | null> {
    const r = await this.pedir('etiquetar', `/${seg(id)}`, { method: 'PATCH', body: { etiqueta } });
    return this.cuerpo<Partial<Dispositivo>>(r);
  }

  static async sonar(id: string): Promise<RespuestaSonar> {
    const r = await this.pedir('sonar', `/${seg(id)}/sonar`, { method: 'POST' });
    const data = await this.cuerpo<RespuestaSonar>(r);
    return { entregado: !!data?.entregado, sockets: Number(data?.sockets ?? 0) };
  }

  static async cerrarSesion(id: string, p: PeticionCierre): Promise<RespuestaCierre> {
    const nota = p.nota?.trim();
    const body: PeticionCierre = nota ? { motivo: p.motivo, nota } : { motivo: p.motivo };
    const r = await this.pedir('cerrar-sesion', `/${seg(id)}/cerrar-sesion`, { method: 'POST', body });
    const data = await this.cuerpo<RespuestaCierre>(r);
    return { socketsCerrados: Number(data?.socketsCerrados ?? 0) };
  }

  static async reactivar(id: string): Promise<void> {
    await this.pedir('reactivar', `/${seg(id)}/reactivar`, { method: 'POST' });
  }
}
